/**
 * runtimeContext
 * ──────────────
 * Central helpers for detecting whether the current visitor is
 * inside a native app wrapper (Despia) or an installed PWA
 * (iOS Add-to-Home-Screen, Android/Desktop installed PWA), versus
 * a plain mobile-web / desktop-web visitor.
 *
 * Any UI or route decision that should differ inside the "app"
 * (skip landing, hide install banners, native-style onboarding,
 * suppress web-only marketing chrome, future: bottom tab bar,
 * native purchase sheet, etc.) MUST branch off `isInApp()` so the
 * web experience stays byte-identical for non-app visitors.
 */

// SSR-safe checks — every accessor guards `typeof window`.

/** True when the visitor is inside the Despia native wrapper. */
export function isDespiaNative() {
  if (typeof navigator === 'undefined') return false;
  return navigator.userAgent.toLowerCase().includes('despia');
}

/** True when the visitor is running the site as an installed PWA. */
export function isStandalonePWA() {
  if (typeof window === 'undefined') return false;
  // iOS Safari sets `navigator.standalone` when launched from Home Screen.
  if (window.navigator && window.navigator.standalone === true) return true;
  // Android Chrome / Desktop installed PWAs advertise `display-mode: standalone`.
  if (typeof window.matchMedia === 'function') {
    try {
      return window.matchMedia('(display-mode: standalone)').matches;
    } catch { /* fall through */ }
  }
  return false;
}

/**
 * The one predicate the app should use whenever it needs to decide
 * "am I in an app-like shell?" — this covers Despia AND standalone
 * PWAs so features degrade gracefully on both.
 */
export function isInApp() {
  return isDespiaNative() || isStandalonePWA();
}

// ─── Onboarding memory ───────────────────────────────────────────
// localStorage flag prevents the mobile-native onboarding carousel
// from replaying on every cold start. Versioned so we can force a
// re-show if the story ever materially changes.

const ONBOARDING_KEY = 'eili_app_onboarding_seen_v1';

export function hasSeenOnboarding() {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(ONBOARDING_KEY) === '1';
  } catch { return false; }
}

export function markOnboardingSeen() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch { /* private mode / storage full — silent */ }
}
